import {
  CloudArrowUp,
  CloudCheck,
  CloudWarning,
  HardDrives,
  FloppyDisk,
} from '@phosphor-icons/react';
import { useEffect, useState, type ComponentType } from 'react';
import { cn } from '@/lib/cn';
import { Tooltip } from '@/components/primitives/tooltip';
import { describeSaveStatus, describeSavedAt, type SaveState, type SaveTone } from './save-status';
import { describeMirror, type MirrorHealthSnapshot } from '@/data/mirror/mirror-health';

const ICONS: Record<string, ComponentType<{ size?: number; weight?: 'regular' | 'bold' }>> = {
  CloudCheck,
  CloudArrowUp,
  CloudWarning,
  HardDrives,
};

const TONE_CLASSES: Record<SaveTone, string> = {
  muted: 'text-text-muted',
  accent: 'text-accent',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
};

/** 相對時間的重算間隔；夠勤於更新，又不會一直讓畫面重繪。 */
const RELATIVE_REFRESH_MS = 30_000;

export function SaveStatusPill({
  state = 'idle',
  lastSavedAt = null,
  onRetry,
  mirror,
  onRetryMirror,
}: {
  state?: SaveState;
  /** 最後一次成功寫入的 UTC 時間。 */
  lastSavedAt?: string | null;
  onRetry?: () => void;
  /** 桌面版才有；網頁版傳 undefined 或 unavailable，這一段完全不渲染。 */
  mirror?: MirrorHealthSnapshot;
  onRetryMirror?: () => void;
}) {
  const status = describeSaveStatus(state);
  const Icon = ICONS[status.icon] ?? CloudCheck;

  // 「3 分鐘前」放著不動會愈來愈不準，定時重算一次。
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!lastSavedAt) return;
    const timer = setInterval(() => setTick((current) => current + 1), RELATIVE_REFRESH_MS);
    return () => clearInterval(timer);
  }, [lastSavedAt]);

  const saved = describeSavedAt(lastSavedAt);
  const detail = saved
    ? `${status.detail}最後一次自動儲存：${saved.full}（${saved.relative}）。`
    : `${status.detail}這次開啟之後還沒有需要儲存的變更。`;

  return (
    <div className="flex shrink-0 items-center gap-1.5 whitespace-nowrap">
      <Tooltip label={detail}>
        <output
          aria-label="儲存狀態"
          aria-live="polite"
          className={cn(
            'inline-flex h-9 items-center gap-2 rounded-full border border-border',
            'bg-surface px-3 text-[0.8125rem] font-medium',
            TONE_CLASSES[status.tone],
          )}
        >
          <Icon size={16} />
          <span>{status.label}</span>
          {saved && (
            <span className="tabular hidden text-[0.75rem] font-normal text-text-muted 2xl:inline">
              {saved.clock}
            </span>
          )}
        </output>
      </Tooltip>
      <MirrorIndicator snapshot={mirror} onRetry={onRetryMirror} />
      {status.retryable && onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="rounded-full px-2 py-1 text-[0.8125rem] font-medium text-accent hover:bg-accent-soft"
        >
          重試
        </button>
      ) : null}
    </div>
  );
}

/**
 * 本機備份（SQLite 鏡像）的狀態（desktop-app.md 6.4）。
 *
 * 正常時只是狀態列旁一個小圖示與 tooltip；連續失敗五次才變紅並給重試。
 * 單次失敗刻意不提示——短暫鎖檔很常見，每次都提示會讓使用者學會忽略。
 */
function MirrorIndicator({
  snapshot,
  onRetry,
}: {
  snapshot?: MirrorHealthSnapshot;
  onRetry?: () => void;
}) {
  const described = snapshot ? describeMirror(snapshot) : null;
  if (!described) return null;

  return (
    <Tooltip label={described.detail}>
      <div className="flex items-center gap-1">
        <output
          aria-label="本機備份狀態"
          className={cn(
            'inline-flex h-9 items-center gap-1.5 rounded-full px-2 text-[0.75rem]',
            described.tone === 'danger'
              ? 'font-medium text-danger'
              : described.tone === 'success'
                ? 'text-success'
                : 'text-text-muted',
          )}
        >
          <FloppyDisk size={15} />
          <span className="tabular hidden 2xl:inline">{described.label}</span>
        </output>
        {described.retryable && onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="rounded-full px-2 py-1 text-[0.8125rem] font-medium text-accent hover:bg-accent-soft"
          >
            重試備份
          </button>
        ) : null}
      </div>
    </Tooltip>
  );
}
